// The Coldroom map → register hand-off (F05 UF-04). The map's Report link and
// its "+ N delivery bins" card used to open the register bare, so a manager
// standing in CR2 tapped Report and then had to pick CR2 again (J8: 2 → 1
// decisions). The link now carries the room and, when a band tile is lit
// (UF-03), the band; the register reads them ONCE into its saved filters and
// drops them from the address, the way the Coldroom page reads ?room=&cell=.
//
// Ruled 8 Oct (APPROVALS.md '## 8 Oct ~09:50'): a map link with a room and NO
// band lit CLEARS the register's saved Severity (every age shows), instead of
// keeping the last saved one. With a band lit, that band is set. A link with
// nothing usable touches nothing, and grower / variety / type are never touched.
import type { AgeBand } from '@/lib/aging'

/** The register's Severity filter values (ColdroomReport `Filters['band']`). */
export type ReportSeverity = 'ALL' | 'warning' | 'critical' | 'over'

const BANDS: readonly AgeBand[] = ['ok', 'warning', 'critical', 'over']

/** The map's lit tile → the register's Severity. Both mean "this band or
 *  worse", so OK — every item — is the register's "Any age". */
function severityFor(band: AgeBand): ReportSeverity {
  return band === 'ok' ? 'ALL' : band
}

/** `/coldroom/report`, carrying the room on screen and the lit band tile. */
export function coldroomReportHref(roomId: string | null, band: '' | AgeBand): string {
  const q = new URLSearchParams()
  if (roomId) q.set('room', roomId)
  if (band) q.set('band', band)
  const s = q.toString()
  return s ? `/coldroom/report?${s}` : '/coldroom/report'
}

/** What a link asks the register to scope to, or null when it carries
 *  nothing usable. An unknown band is ignored rather than guessed. */
export function reportScopeFromParams(
  params: URLSearchParams,
): { room?: string; band?: ReportSeverity } | null {
  const scope: { room?: string; band?: ReportSeverity } = {}
  const room = params.get('room')?.trim()
  if (room) scope.room = room
  const band = params.get('band') as AgeBand | null
  if (band && BANDS.includes(band)) scope.band = severityFor(band)
  // A map link (it always names a room) with no usable band: the manager
  // lit no tile, so Severity must not stay on whatever was last saved.
  else if (scope.room) scope.band = 'ALL'
  return Object.keys(scope).length > 0 ? scope : null
}
